import React, { useState } from 'react';
import { Typography, Tabs, Space, Spin, Tag, Input, Empty, Button } from 'antd';
import {
    BgColorsOutlined,
    AppstoreOutlined,
    BlockOutlined,
    BuildOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined,
    SearchOutlined,
    EyeOutlined,
    UnorderedListOutlined,
    DragOutlined,
    EditOutlined,
    PlusOutlined,
    LayoutOutlined
} from '@ant-design/icons';
import { useDrag } from 'react-dnd';
import ComponentEditor from './ComponentEditor';
import TokenEditorModal from './TokenEditorModal';
import ComponentCreator from './ComponentCreator';
import TemplateEditor from './TemplateEditor';
import api from '../../../api/client';

const { Text, Title } = Typography;

// Premium component card for gallery view
const ComponentCard = ({ component, isCompatible, viewMode, onEdit }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'DESIGN_KIT_COMPONENT',
        item: { componentType: component.type, componentId: component.id },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    }), [component]);

    const deviceIcons = {
        mobile: <MobileOutlined />,
        tablet: <TabletOutlined />,
        desktop: <DesktopOutlined />
    };

    // Layer colors
    const layerColors = {
        atoms: { bg: 'rgba(255, 166, 87, 0.1)', border: '#ffa657', icon: '#ffa657' },
        molecules: { bg: 'rgba(126, 231, 135, 0.1)', border: '#7ee787', icon: '#7ee787' },
        organisms: { bg: 'rgba(163, 113, 247, 0.1)', border: '#a371f7', icon: '#a371f7' }
    };

    const layerStyle = layerColors[component.layer] || layerColors.atoms;

    if (viewMode === 'grid') {
        return (
            <div
                ref={drag}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    background: isDragging ? 'rgba(88, 166, 255, 0.15)' : '#0d1117',
                    border: isDragging ? '2px solid #58a6ff' : `1px solid ${layerStyle.border}40`,
                    borderRadius: 12,
                    cursor: isCompatible ? 'grab' : 'not-allowed',
                    opacity: isDragging ? 0.7 : isCompatible ? 1 : 0.4,
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                {/* Preview Area */}
                <div style={{
                    background: layerStyle.bg,
                    padding: '20px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 80,
                    borderBottom: `1px solid ${layerStyle.border}30`
                }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: `${layerStyle.border}30`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: layerStyle.icon,
                        fontSize: 18
                    }}>
                        {component.layer === 'organisms' ? <BuildOutlined /> :
                            component.layer === 'molecules' ? <BlockOutlined /> :
                                <AppstoreOutlined />}
                    </div>
                </div>

                {/* Info Area */}
                <div style={{ padding: '12px 14px' }}>
                    <Text style={{
                        color: '#e6edf3',
                        fontSize: 13,
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: 6
                    }}>
                        {component.name}
                    </Text>

                    {/* Device icons */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {component.devices?.map(device => (
                            <span
                                key={device}
                                style={{
                                    color: '#58a6ff',
                                    fontSize: 11
                                }}
                            >
                                {deviceIcons[device]}
                            </span>
                        ))}
                    </div>

                    {/* Variants or States */}
                    {component.variants?.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {component.variants.slice(0, 2).map(v => (
                                <span key={v} style={{
                                    fontSize: 10,
                                    color: '#8b949e',
                                    background: '#21262d',
                                    padding: '2px 6px',
                                    borderRadius: 4
                                }}>
                                    {v}
                                </span>
                            ))}
                            {component.variants.length > 2 && (
                                <span style={{ fontSize: 10, color: '#484f58' }}>
                                    +{component.variants.length - 2}
                                </span>
                            )}
                        </div>
                    )}

                    {component.states?.length > 1 && (
                        <Text style={{ color: '#484f58', fontSize: 10 }}>
                            {component.states.length} states
                        </Text>
                    )}
                </div>

                {/* Drag indicator and Edit button */}
                <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 6
                }}>
                    {(component.layer === 'molecules' || component.layer === 'organisms') && isHovered && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(component);
                            }}
                            style={{
                                width: 24,
                                height: 24,
                                borderRadius: 4,
                                background: '#58a6ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: '#fff',
                                fontSize: 12
                            }}
                        >
                            <EditOutlined />
                        </div>
                    )}
                    <DragOutlined style={{ color: '#484f58', fontSize: 12 }} />
                </div>
            </div>
        );
    }

    // List view
    return (
        <div
            ref={drag}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                background: isDragging ? 'rgba(88, 166, 255, 0.1)' : '#0d1117',
                border: `1px solid ${layerStyle.border}40`,
                borderRadius: 8,
                cursor: isCompatible ? 'grab' : 'not-allowed',
                opacity: isDragging ? 0.7 : isCompatible ? 1 : 0.4,
                marginBottom: 8,
                transition: 'all 0.2s ease'
            }}
        >
            {/* Icon */}
            <div style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: layerStyle.bg,
                border: `1px solid ${layerStyle.border}50`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: layerStyle.icon,
                fontSize: 16,
                flexShrink: 0
            }}>
                {component.layer === 'organisms' ? <BuildOutlined /> :
                    component.layer === 'molecules' ? <BlockOutlined /> :
                        <AppstoreOutlined />}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{
                    color: '#e6edf3',
                    fontSize: 13,
                    fontWeight: 500,
                    display: 'block'
                }}>
                    {component.name}
                </Text>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <Space size={4}>
                        {component.devices?.map(device => (
                            <span key={device} style={{ color: '#58a6ff', fontSize: 10 }}>
                                {deviceIcons[device]}
                            </span>
                        ))}
                    </Space>
                    {component.variants?.length > 0 && (
                        <Text style={{ color: '#484f58', fontSize: 10 }}>
                            {component.variants.length} variants
                        </Text>
                    )}
                    {component.states?.length > 1 && (
                        <Text style={{ color: '#484f58', fontSize: 10 }}>
                            {component.states.length} states
                        </Text>
                    )}
                </div>
            </div>

            {/* Drag handle */}
            <DragOutlined style={{ color: '#484f58', fontSize: 12 }} />
        </div>
    );
};

// Token card with premium styling and edit support
const TokenCard = ({ name, value, usage, type, token, isCustom, onEdit }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                background: '#0d1117',
                border: isCustom ? '1px solid #58a6ff40' : '1px solid #21262d',
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                transition: 'all 0.2s ease',
                position: 'relative',
                cursor: 'pointer'
            }}
            onClick={() => onEdit?.(token, type)}
        >
            {/* Color preview */}
            {type === 'color' && value && (
                <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: value,
                    border: '2px solid #21262d',
                    flexShrink: 0,
                    boxShadow: `0 0 12px ${value}40`
                }} />
            )}

            {/* Spacing preview */}
            {type === 'spacing' && (
                <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#161b22',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <div style={{
                        width: Math.min(value || 8, 24),
                        height: Math.min(value || 8, 24),
                        background: '#58a6ff',
                        borderRadius: 4
                    }} />
                </div>
            )}

            {/* Typography preview */}
            {type === 'typography' && (
                <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#161b22',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 16,
                    fontFamily: token?.fontFamily || 'Inter, sans-serif',
                    fontWeight: token?.weight || 600,
                    color: '#58a6ff'
                }}>
                    Aa
                </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Text style={{
                        color: '#e6edf3',
                        fontSize: 13,
                        fontWeight: 500,
                        display: 'block',
                        textTransform: 'capitalize'
                    }}>
                        {name}
                    </Text>
                    {isCustom && (
                        <Tag style={{ fontSize: 9, padding: '0 4px', margin: 0, background: '#58a6ff20', border: 'none', color: '#58a6ff' }}>
                            Custom
                        </Tag>
                    )}
                </div>
                <Text style={{ color: '#58a6ff', fontSize: 11, display: 'block' }}>
                    {value}
                </Text>
                {/* Typography sample text */}
                {type === 'typography' && token && (
                    <div style={{
                        marginTop: 6,
                        padding: '6px 8px',
                        background: '#161b22',
                        borderRadius: 4,
                        fontFamily: token.fontFamily || 'Inter, sans-serif',
                        fontWeight: token.weight || 400,
                        fontSize: 14,
                        color: '#e6edf3'
                    }}>
                        The quick brown fox jumps...
                    </div>
                )}
            </div>

            {usage && type !== 'typography' && (
                <Text style={{ color: '#484f58', fontSize: 10, maxWidth: 60 }} ellipsis>
                    {usage}
                </Text>
            )}

            {/* Edit icon on hover */}
            {isHovered && (
                <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    background: isCustom ? '#58a6ff' : '#7ee787',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 10
                }}>
                    <EditOutlined />
                </div>
            )}
        </div>
    );
};

// Tokens Tab with premium layout and create/edit support
const TokensTab = ({ designKit, activeDeviceType, panelWidth, onEditToken, onCreateToken }) => {
    const numColumns = panelWidth && panelWidth > 400 ? 2 : 1;

    const SectionHeader = ({ title, category }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={{
                color: '#8b949e',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '1px'
            }}>
                {title}
            </Text>
            <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => onCreateToken?.(category)}
                style={{ color: '#58a6ff', fontSize: 11, padding: '2px 6px', height: 'auto' }}
            >
                Add
            </Button>
        </div>
    );

    return (
        <div style={{ padding: '16px 12px', minHeight: '100%', background: '#0d1117' }}>
            {/* Colors */}
            <SectionHeader title="COLORS" category="color" />
            <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                gap: 10,
                marginBottom: 24
            }}>
                {designKit.tokens?.colors?.map(color => (
                    <TokenCard
                        key={color.id || color.name}
                        name={color.name}
                        value={color.value}
                        usage={color.usage}
                        type="color"
                        token={color}
                        isCustom={color.isCustom}
                        onEdit={onEditToken}
                    />
                ))}
            </div>

            {/* Typography */}
            <SectionHeader title="TYPOGRAPHY" category="typography" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                {designKit.tokens?.typography?.map(typo => (
                    <TokenCard
                        key={typo.id || typo.name}
                        name={typo.name}
                        value={`${typo.fontFamily} · ${typo.sizes?.[activeDeviceType] || typo.sizes?.mobile}px`}
                        usage={`Weight: ${typo.weight}`}
                        type="typography"
                        token={typo}
                        isCustom={typo.isCustom}
                        onEdit={onEditToken}
                    />
                ))}
            </div>

            {/* Spacing */}
            <SectionHeader title="SPACING" category="spacing" />
            <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                gap: 10
            }}>
                {designKit.tokens?.spacing?.map(space => (
                    <TokenCard
                        key={space.id || space.name}
                        name={space.name}
                        value={`${space.values?.[activeDeviceType] || space.values?.mobile}px`}
                        type="spacing"
                        token={space}
                        isCustom={space.isCustom}
                        onEdit={onEditToken}
                    />
                ))}
            </div>
        </div>
    );
};

// Component Gallery Tab
const ComponentGalleryTab = ({ components, isCompatible, viewMode, panelWidth, onEdit }) => {
    if (!components || components.length === 0) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Text style={{ color: '#484f58' }}>No components found</Text>
            </div>
        );
    }

    const numColumns = panelWidth && panelWidth > 400 ? 2 : 1;

    if (viewMode === 'grid') {
        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                gap: 12,
                padding: '16px 12px'
            }}>
                {components.map(component => (
                    <ComponentCard
                        key={component.id}
                        component={component}
                        isCompatible={isCompatible(component)}
                        viewMode="grid"
                        onEdit={onEdit}
                    />
                ))}
            </div>
        );
    }

    return (
        <div style={{ padding: '16px 12px' }}>
            {components.map(component => (
                <ComponentCard
                    key={component.id}
                    component={component}
                    isCompatible={isCompatible(component)}
                    viewMode="list"
                    onEdit={onEdit}
                />
            ))}
        </div>
    );
};

/**
 * Design Kit Panel - Premium component gallery with horizontal tabs
 */
const DesignKitPanel = ({ designKit, isLoading, activeDeviceType, panelWidth, onComponentUpdate, onTokenCreate, onTokenUpdate, onTokenDelete, canvasId, onRefresh }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('atoms');
    const [viewMode, setViewMode] = useState('grid');
    const [editingComponent, setEditingComponent] = useState(null);

    // Token editor state
    const [tokenEditorVisible, setTokenEditorVisible] = useState(false);
    const [editingToken, setEditingToken] = useState(null);
    const [tokenCategory, setTokenCategory] = useState(null);

    // Handle opening component editor
    const handleEditComponent = (component) => {
        setEditingComponent(component);
    };

    // Handle saving component changes
    const handleSaveComponent = async (componentId, updates) => {
        console.log('Saving component:', componentId, updates);
        try {
            // Make API call to update the component
            await api.put(`/design-kit/components/${componentId}`, updates);
            console.log('Component saved successfully');
            // Refresh the design kit so the canvas preview updates
            onRefresh?.();
        } catch (error) {
            console.error('Failed to save component:', error);
        }
    };

    // Handle closing editor
    const handleCloseEditor = () => {
        setEditingComponent(null);
    };

    // Token handlers
    const handleEditToken = (token, category) => {
        setEditingToken(token);
        setTokenCategory(category);
        setTokenEditorVisible(true);
    };

    const handleCreateToken = (category) => {
        setEditingToken(null);
        setTokenCategory(category);
        setTokenEditorVisible(true);
    };

    const handleCloseTokenEditor = () => {
        setTokenEditorVisible(false);
        setEditingToken(null);
        setTokenCategory(null);
    };

    const handleSaveToken = async (tokenData, isEditing) => {
        console.log('Saving token:', { tokenData, isEditing, editingToken, tokenCategory, canvasId });

        try {
            if (isEditing && editingToken?.id) {
                // Update existing custom token
                const response = await api.put(`/design-kit/tokens/${editingToken.id}`, {
                    ...tokenData,
                    category: tokenCategory
                });
                console.log('Token updated:', response.data);
                onTokenUpdate?.(editingToken.id, tokenData);
            } else if (isEditing && !editingToken?.id) {
                // Editing a default token - create an override (new custom token with same name)
                const response = await api.post('/design-kit/tokens', {
                    canvasId,
                    category: tokenCategory,
                    ...tokenData
                });
                console.log('Token override created:', response.data);
                onTokenCreate?.(canvasId, tokenCategory, tokenData);
            } else {
                // Create new token
                const response = await api.post('/design-kit/tokens', {
                    canvasId,
                    category: tokenCategory,
                    ...tokenData
                });
                console.log('Token created:', response.data);
                onTokenCreate?.(canvasId, tokenCategory, tokenData);
            }
            // Refresh the design kit to show new token
            onRefresh?.();
        } catch (error) {
            console.error('Failed to save token:', error);
            throw error;
        }
    };

    const handleDeleteToken = async (tokenId) => {
        console.log('Deleting token:', tokenId);
        await api.delete(`/design-kit/tokens/${tokenId}`);
        onTokenDelete?.(tokenId);
        // Refresh the design kit
        onRefresh?.();
    };

    // Component creator state and handlers
    const [componentCreatorVisible, setComponentCreatorVisible] = useState(false);
    const [componentCreatorLayer, setComponentCreatorLayer] = useState(null);

    const handleOpenComponentCreator = (layer) => {
        setComponentCreatorLayer(layer);
        setComponentCreatorVisible(true);
    };

    const handleCloseComponentCreator = () => {
        setComponentCreatorVisible(false);
        setComponentCreatorLayer(null);
    };

    const handleCreateComponent = async (layer, componentData) => {
        console.log('Creating component:', { layer, componentData, canvasId });

        // Transform states from string array to object array format expected by backend
        const transformedStates = componentData.states?.map(state =>
            typeof state === 'string' ? { name: state, overrides: {} } : state
        ) || [{ name: 'default', overrides: {} }];

        const response = await api.post('/design-kit/components', {
            canvasId,
            type: componentData.type,
            layer,
            name: componentData.name,
            devices: componentData.devices,
            variants: componentData.variants,
            states: transformedStates,
            defaultWidth: componentData.defaultWidth,
            defaultHeight: componentData.defaultHeight,
            props: componentData.props
        });
        console.log('Component created:', response.data);
        // Refresh design kit to show new component
        onRefresh?.();
    };

    if (isLoading) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Spin />
                <Text style={{ color: '#8b949e', display: 'block', marginTop: 12 }}>
                    Loading design kit...
                </Text>
            </div>
        );
    }

    if (!designKit) {
        return (
            <div style={{ padding: 40 }}>
                <Empty
                    description={<Text style={{ color: '#8b949e' }}>No design kit available</Text>}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        );
    }

    const filterComponents = (components) => {
        if (!searchQuery) return components || [];
        return (components || []).filter(c =>
            c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.type.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const isCompatible = (component) => {
        if (!component.devices) return true;
        return component.devices.includes(activeDeviceType);
    };

    const filteredAtoms = filterComponents(designKit.components?.atoms);
    const filteredMolecules = filterComponents(designKit.components?.molecules);
    const filteredOrganisms = filterComponents(designKit.components?.organisms);
    const filteredTemplates = filterComponents(designKit.components?.templates);

    const tabItems = [
        {
            key: 'tokens',
            label: <span style={{ padding: '0 4px' }}>Tokens</span>,
            children: (
                <TokensTab
                    designKit={designKit}
                    activeDeviceType={activeDeviceType}
                    panelWidth={panelWidth}
                    onEditToken={handleEditToken}
                    onCreateToken={handleCreateToken}
                />
            )
        },
        {
            key: 'atoms',
            label: (
                <Space size={6}>
                    <span>Atoms</span>
                    <span style={{
                        background: '#21262d',
                        color: '#8b949e',
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 10
                    }}>
                        {filteredAtoms.length}
                    </span>
                    <PlusOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenComponentCreator('atoms');
                        }}
                        style={{
                            color: '#8b949e',
                            fontSize: 12,
                            cursor: 'pointer',
                            marginLeft: 4
                        }}
                    />
                </Space>
            ),
            children: (
                <ComponentGalleryTab
                    components={filteredAtoms}
                    isCompatible={isCompatible}
                    viewMode={viewMode}
                    panelWidth={panelWidth}
                    onEdit={handleEditComponent}
                />
            )
        },
        {
            key: 'molecules',
            label: (
                <Space size={6}>
                    <span>Molecules</span>
                    <span style={{
                        background: '#21262d',
                        color: '#8b949e',
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 10
                    }}>
                        {filteredMolecules.length}
                    </span>
                    <PlusOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenComponentCreator('molecules');
                        }}
                        style={{
                            color: '#8b949e',
                            fontSize: 12,
                            cursor: 'pointer',
                            marginLeft: 4
                        }}
                    />
                </Space>
            ),
            children: (
                <ComponentGalleryTab
                    components={filteredMolecules}
                    isCompatible={isCompatible}
                    viewMode={viewMode}
                    panelWidth={panelWidth}
                    onEdit={handleEditComponent}
                />
            )
        },
        {
            key: 'organisms',
            label: (
                <Space size={6}>
                    <span>Organisms</span>
                    <span style={{
                        background: '#21262d',
                        color: '#8b949e',
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 10
                    }}>
                        {filteredOrganisms.length}
                    </span>
                    <PlusOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenComponentCreator('organisms');
                        }}
                        style={{
                            color: '#8b949e',
                            fontSize: 12,
                            cursor: 'pointer',
                            marginLeft: 4
                        }}
                    />
                </Space>
            ),
            children: (
                <ComponentGalleryTab
                    components={filteredOrganisms}
                    isCompatible={isCompatible}
                    viewMode={viewMode}
                    panelWidth={panelWidth}
                    onEdit={handleEditComponent}
                />
            )
        }
    ];

    // Add Templates tab
    tabItems.push({
        key: 'templates',
        label: (
            <Space size={6}>
                <LayoutOutlined />
                <span>Templates</span>
                <span style={{
                    background: '#21262d',
                    color: '#8b949e',
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 10
                }}>
                    {filteredTemplates.length}
                </span>
                <PlusOutlined
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenComponentCreator('templates');
                    }}
                    style={{
                        color: '#8b949e',
                        fontSize: 12,
                        cursor: 'pointer',
                        marginLeft: 4
                    }}
                />
            </Space>
        ),
        children: (
            <ComponentGalleryTab
                components={filteredTemplates}
                isCompatible={isCompatible}
                viewMode={viewMode}
                panelWidth={panelWidth}
                onEdit={handleEditComponent}
            />
        )
    });

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
            {/* Header */}
            <div style={{
                padding: '16px 16px 14px',
                background: '#161b22',
                borderBottom: '1px solid #21262d'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <Title level={5} style={{ margin: 0, color: '#e6edf3', fontSize: 15 }}>
                        Design Kit
                    </Title>
                    <div style={{ display: 'flex', gap: 4 }}>
                        <div
                            onClick={() => setViewMode('grid')}
                            style={{
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: viewMode === 'grid' ? '#58a6ff' : '#484f58',
                                borderRadius: 6,
                                background: viewMode === 'grid' ? 'rgba(88, 166, 255, 0.15)' : 'transparent',
                                transition: 'all 0.2s'
                            }}
                        >
                            <AppstoreOutlined style={{ fontSize: 14 }} />
                        </div>
                        <div
                            onClick={() => setViewMode('list')}
                            style={{
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: viewMode === 'list' ? '#58a6ff' : '#484f58',
                                borderRadius: 6,
                                background: viewMode === 'list' ? 'rgba(88, 166, 255, 0.15)' : 'transparent',
                                transition: 'all 0.2s'
                            }}
                        >
                            <UnorderedListOutlined style={{ fontSize: 14 }} />
                        </div>
                    </div>
                </div>
                <Input
                    placeholder="Search components..."
                    prefix={<SearchOutlined style={{ color: '#484f58' }} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        background: '#0d1117',
                        border: '1px solid #21262d',
                        borderRadius: 8,
                        height: 36
                    }}
                />
            </div>

            {/* Tabs */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="small"
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                tabBarStyle={{
                    margin: 0,
                    padding: '0 12px',
                    background: '#161b22',
                    borderBottom: '1px solid #21262d'
                }}
            />

            {/* Component Editor Drawer */}
            <ComponentEditor
                visible={!!editingComponent}
                component={editingComponent}
                designKit={designKit}
                onClose={handleCloseEditor}
                onSave={handleSaveComponent}
            />

            {/* Token Editor Modal */}
            <TokenEditorModal
                visible={tokenEditorVisible}
                token={editingToken}
                category={tokenCategory}
                canvasId={canvasId}
                onClose={handleCloseTokenEditor}
                onSave={handleSaveToken}
                onDelete={handleDeleteToken}
            />

            {/* Component Creator Modal */}
            <ComponentCreator
                visible={componentCreatorVisible}
                layer={componentCreatorLayer}
                canvasId={canvasId}
                designKit={designKit}
                onClose={handleCloseComponentCreator}
                onCreate={handleCreateComponent}
            />
        </div>
    );
};

export default DesignKitPanel;
